import React, { ReactNode } from 'react';
import { Alert, Grid } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PaymentCapabilitiesData } from '../../api';
import { usePaymentCapabilities } from '../payments/usePaymentCapabilities';

type ProviderCapabilityGateProps = {
  capability: keyof PaymentCapabilitiesData;
  // Receives the extra `disabled` constraint imposed by the gate, so the caller can OR it
  // with its own (readOnly, isSubmitting, ...).
  children: (capabilityDisabled: boolean) => ReactNode;
  // Override the default "saving cards is not supported" copy.
  noteKey?: string;
  // When set and the account is locked by sales tax, the control is disabled and this note replaces
  // the provider note.
  taxLockedNoteKey?: string;
  // Layout for the wrapper, for callers whose gated control needs to keep its own flex sizing.
  sx?: SxProps<Theme>;
};

// Renders a feature that only works when the connected payment provider supports it. Everyone sees
// the control -- accounts without the capability get it disabled with a note explaining why,
// rather than having it hidden.
export const ProviderCapabilityGate = ({
  capability,
  children,
  noteKey,
  taxLockedNoteKey,
  sx,
}: ProviderCapabilityGateProps) => {
  const { t } = useTranslation();
  const capabilities = usePaymentCapabilities();
  const isTaxLocked = !!taxLockedNoteKey && capabilities.sales_tax_locked;
  const isSupported = !!capabilities[capability] && !isTaxLocked;

  return (
    <Grid container flexDirection="column" alignItems="flex-start" sx={sx}>
      {children(!isSupported)}
      {!isSupported && (
        <Alert severity="info" sx={{ mt: 1 }}>
          {isTaxLocked ? t(taxLockedNoteKey) : t(noteKey || 'paymentGate.saveCardUnsupported')}
        </Alert>
      )}
    </Grid>
  );
};
